import { useState, useEffect } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CONTACT_CADENCES_QUERY } from '../utils/cadence-queries';

/**
 * Fetch all cadences a contact is or has been enrolled in.
 * Returns { cadences, loading, error, refetch }.
 *
 * @param {string|number} contactId - The contact's ID
 */
const useContactCadences = (contactId) => {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!contactId) {
      // Clear stale state so consumers don't render the prior contact's cadences
      // after the parent disables the hook (e.g. a flag flips off).
      setCadences([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Drop the prior contact's data so the next-contact load doesn't briefly
    // render stale pills while the new fetch is in flight.
    setCadences([]);

    cadenceGqlCall(CONTACT_CADENCES_QUERY, { contactId: String(contactId) })
      .then((data) => {
        if (cancelled) return;
        setCadences(data.contactCadences || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to fetch cadence history');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contactId, fetchKey]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { cadences, loading, error, refetch };
};

export default useContactCadences;
